import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Button, Card, Dropdown, Flex, Input, message, QRCode, Space, Typography } from 'antd'
import { CopyOutlined, DownOutlined, DownloadOutlined, ExportOutlined, PrinterOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { ManagedForm } from '../public-form'
import { FormLinkPreview } from './form-link-preview'
import { assetUrl } from '../api'

type FormSharingProps = { form: ManagedForm; unpublishedChanges?: boolean; onUnpublish?: () => void; pending?: boolean }
export function FormSharing({ form, unpublishedChanges, onUnpublish, pending }: FormSharingProps) {
    const { t } = useTranslation()
    const [notice, contextHolder] = message.useMessage()
    const qr = useRef<HTMLDivElement>(null)
    const [error, setError] = useState(false)
    const shareable = form.enabled && form.ready
    const config = form.published ?? form.draft
    const download = async (type: 'svg' | 'png') => {
        setError(false)
        const svg = qr.current?.querySelector('svg')
        if (!svg) return
        const exported = svg.cloneNode(true) as SVGSVGElement
        const modules = svg.viewBox.baseVal.width
        exported.setAttribute('viewBox', `-4 -4 ${modules + 8} ${modules + 8}`)
        const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        background.setAttribute('x', '-4')
        background.setAttribute('y', '-4')
        background.setAttribute('width', String(modules + 8))
        background.setAttribute('height', String(modules + 8))
        background.setAttribute('fill', '#ffffff')
        exported.insertBefore(background, exported.firstChild)
        const blob = new Blob([new XMLSerializer().serializeToString(exported)], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)
        try {
            let href = url
            if (type === 'png') {
                const image = new Image()
                await new Promise<void>((resolve, reject) => {
                    image.onload = () => resolve()
                    image.onerror = reject
                    image.src = url
                })
                const canvas = document.createElement('canvas')
                canvas.width = 1024
                canvas.height = 1024
                canvas.getContext('2d')?.drawImage(image, 0, 0, 1024, 1024)
                href = canvas.toDataURL('image/png')
            }
            const link = document.createElement('a')
            link.href = href
            link.download = `application-qr.${type}`
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch {
            setError(true)
        } finally {
            window.setTimeout(() => URL.revokeObjectURL(url), 5000)
        }
    }
    return (
        <Flex
            vertical
            gap="middle"
        >
            {!shareable && (
                <Alert
                    type="info"
                    showIcon
                    message={t(
                        'Publish the form and resolve any campaign prerequisites before sharing its link or QR code.'
                    )}
                />
            )}
            {contextHolder}
            <section>
                <Typography.Title level={4}>{t('Share your form')}</Typography.Title>
                <Typography.Paragraph type="secondary">
                    {t('Share this link so candidates can apply.')}
                </Typography.Paragraph>
                <Flex
                    gap="small"
                    align="center"
                    wrap
                    className="public-form-share-link"
                >
                    <Space.Compact className="public-form-copy-control min-w-0">
                        <Input
                            aria-label={t('Public form link')}
                            value={form.url}
                            readOnly
                            className="min-w-0"
                        />
                        <Button
                            type="primary"
                            icon={<CopyOutlined />}
                            disabled={!shareable}
                            onClick={() => {
                                navigator.clipboard
                                    .writeText(form.url)
                                    .then(() => {
                                        void notice.success(t('Link copied'))
                                    })
                                    .catch(() => {
                                        void notice.error(t('Could not copy link'))
                                    })
                            }}
                        >
                            {t('Copy link')}
                        </Button>
                    </Space.Compact>
                    <Button
                        type="link"
                        icon={<ExportOutlined />}
                        href={form.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!shareable}
                    >
                        {t('Open form')}
                    </Button>
                </Flex>
                <Typography.Paragraph
                    type="secondary"
                    className="mt-2 mb-0"
                >
                    {t('This link stays the same when you update the form.')}
                </Typography.Paragraph>
            </section>
            <section aria-label={t('Link preview')}>
                <Typography.Title level={4}>{t('Link preview')}</Typography.Title>
                <Typography.Paragraph type="secondary">
                    {t('This is what your link looks like when you share it.')}
                </Typography.Paragraph>
                <FormLinkPreview form={form} />
                {form.enabled && form.published && unpublishedChanges && (
                    <Typography.Paragraph
                        type="secondary"
                        className="mt-2 mb-0"
                    >
                        {t('The preview uses the published form. Publish your changes to update it.')}
                    </Typography.Paragraph>
                )}
            </section>
            <Card size="small">
                <div className="public-form-sharing-qr">
                    <div
                        ref={qr}
                        className="w-fit rounded-lg border border-gray-200 bg-white p-2"
                    >
                        <QRCode
                            type="svg"
                            value={form.url}
                            color="#000000"
                            bgColor="#ffffff"
                            size={144}
                            bordered={false}
                        />
                    </div>
                    <Flex
                        vertical
                        gap="small"
                        justify="center"
                        className="min-w-0"
                    >
                        <Typography.Title
                            level={5}
                            className="mb-0"
                        >
                            {t('QR code for print')}
                        </Typography.Title>
                        <Typography.Paragraph
                            type="secondary"
                            className="mb-0"
                        >
                            {t('Use this code on posters, flyers, or at your location.')}
                        </Typography.Paragraph>
                        <Flex
                            gap="small"
                            wrap
                        >
                            <Dropdown.Button
                                className="w-fit"
                                icon={<DownOutlined />}
                                buttonsRender={([downloadButton, formatButton]) => [
                                    downloadButton,
                                    React.cloneElement(formatButton as React.ReactElement<{ 'aria-label'?: string }>, {
                                        'aria-label': t('Download format'),
                                    }),
                                ]}
                                disabled={!shareable}
                                onClick={() => void download('png')}
                                menu={{
                                    items: [
                                        { key: 'png', label: 'PNG' },
                                        { key: 'svg', label: 'SVG' },
                                    ],
                                    onClick: ({ key }) => void download(key as 'png' | 'svg'),
                                }}
                            >
                                <DownloadOutlined /> {t('Download QR')}
                            </Dropdown.Button>
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={() => window.print()}
                                disabled={!shareable}
                            >
                                {t('Print QR')}
                            </Button>
                        </Flex>
                        {error && (
                            <Typography.Text
                                type="danger"
                                role="alert"
                            >
                                {t('Could not download QR code')}
                            </Typography.Text>
                        )}
                    </Flex>
                </div>
            </Card>
            {form.enabled && onUnpublish && (
                <Flex
                    justify="end"
                    className="border-t border-gray-200 pt-3"
                >
                    <Button
                        type="text"
                        danger
                        onClick={onUnpublish}
                        loading={pending}
                    >
                        {t('Unpublish form')}
                    </Button>
                </Flex>
            )}
            {createPortal(
                <div className="public-form-print">
                    {config.logoUrl && (
                        <img
                            src={assetUrl(config.logoUrl)}
                            alt={config.brandName}
                            className="mx-auto max-h-20 max-w-[220px]"
                        />
                    )}
                    <h2>{config.brandName}</h2>
                    <h1>{config.heading}</h1>
                    <p>{t('Scan to apply and start a WhatsApp conversation.')}</p>
                    <QRCode
                        type="svg"
                        value={form.url}
                        size={300}
                        color="#000000"
                        bgColor="#ffffff"
                        bordered={false}
                    />
                    <p>{form.url}</p>
                </div>,
                document.body
            )}
        </Flex>
    )
}
